import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import TitleWithBack from '../../components/common/TitleWithBack';
import { formatDate } from '../../utils/helpers';

function SeriesEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '',
    startDate: '',
    endDate: '',
    eimsFetchEnabled: false
  });
  const [loading, setLoading] = useState(false);
  const [tooltip, setTooltip] = useState('');

  // States for program editing
  const [programs, setPrograms] = useState([]);
  const [years, setYears] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [addedPrograms, setAddedPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedSemester, setSelectedSemester] = useState('');

  // State for filtered semesters
  const [filteredSemesters, setFilteredSemesters] = useState([]);

  // Fetch available programs, years, semesters
  useEffect(() => {
    async function fetchData() {
      try {
        const [progRes, yearRes, semRes] = await Promise.all([
          axios.get('/api/programs'),
          axios.get('/api/years-of-study'),
          axios.get('/api/semesters')
        ]);
        const progData = Array.isArray(progRes.data) ? progRes.data : progRes.data.data || [];
        const yearData = Array.isArray(yearRes.data) ? yearRes.data : yearRes.data.data || [];
        const semData = Array.isArray(semRes.data) ? semRes.data : semRes.data.data || [];
        setPrograms(progData);
        setYears(yearData);
        setSemesters(semData);
        setFilteredSemesters(semData); // Initially show all semesters
      } catch (err) {
        // Handle error if needed
      }
    }
    fetchData();
  }, []);

  // Filter semesters based on selected program and year
  useEffect(() => {
    if (selectedProgram && selectedYear) {
      // Find all semesters already used for this program+year
      const usedSemesterIds = addedPrograms
        .filter(
          item =>
            item.programId === parseInt(selectedProgram) &&
            item.yearOfStudyId === parseInt(selectedYear)
        )
        .map(item => item.semesterId);
    
      // Show only semesters NOT already used
      const filtered = semesters.filter(sem => !usedSemesterIds.includes(sem.id));
      setFilteredSemesters(filtered);
    
      // If the currently selected semester is now invalid, reset it
      if (!filtered.some(sem => sem.id === parseInt(selectedSemester))) {
        setSelectedSemester('');
      }
    } else {
      setFilteredSemesters(semesters);
      setSelectedSemester('');
    }
  }, [selectedProgram, selectedYear, addedPrograms, semesters]);

  // Fetch series details (including associated programs if any)
  useEffect(() => {
    async function fetchSeries() {
      setLoading(true);
      try {
        const res = await axios.get(`/api/exam-series/${id}`);
        const data = res.data;
        console.log('Series data received:', data); // Debug log
        
        setForm({
          name: data.name,
          startDate: data.startDate,
          endDate: data.endDate,
          eimsFetchEnabled: !!data.eimsFetchEnabled
        });
        
        // Handle the new seriesPrograms structure
        if (data.seriesPrograms && Array.isArray(data.seriesPrograms)) {
          // Map seriesPrograms to the format expected by the form
          const progs = data.seriesPrograms.map(sp => ({
            programId: sp.program?.id,
            yearOfStudyId: sp.yearOfStudy?.id,
            semesterId: sp.semester?.id
          })).filter(item => item.programId && item.yearOfStudyId && item.semesterId);
          
          console.log('Mapped seriesPrograms:', progs); // Debug log
          setAddedPrograms(progs);
        } else if (data.programs && Array.isArray(data.programs)) {
          // Fallback for legacy data structure
          const progs = data.programs.map(p => ({
            programId: p.id,
            yearOfStudyId: data.yearOfStudyId,
            semesterId: data.semesterId
          }));
          console.log('Mapped legacy programs:', progs); // Debug log
          setAddedPrograms(progs);
        }
      } catch (err) {
        console.error('Error fetching series:', err); // Debug log
        setTooltip('Error fetching series details.');
      }
      setLoading(false);
    }
    fetchSeries();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleProgramChange = (e) => {
    setSelectedProgram(e.target.value);
  };

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  const handleSemesterChange = (e) => {
    setSelectedSemester(e.target.value);
  };

  const handleAddProgram = () => {
    if (!selectedProgram || !selectedYear || !selectedSemester) return;
    
    // Check if this combination already exists
    const existingIndex = addedPrograms.findIndex(
      item => item.programId === parseInt(selectedProgram) && 
              item.yearOfStudyId === parseInt(selectedYear) &&
              item.semesterId === parseInt(selectedSemester)
    );

    if (existingIndex !== -1) {
      setTooltip('This program-year-semester combination already exists.');
      return;
    }

    const newEntry = {
      programId: parseInt(selectedProgram, 10),
      yearOfStudyId: parseInt(selectedYear, 10),
      semesterId: parseInt(selectedSemester, 10)
    };
    setAddedPrograms(prev => [...prev, newEntry]);
    setSelectedProgram('');
    setSelectedYear('');
    setSelectedSemester('');
    setTooltip(''); // Clear any previous error messages
  };

  const handleRemoveProgram = (index) => {
    setAddedPrograms(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (addedPrograms.length === 0) {
      setTooltip('Please add at least one program with year and semester.');
      return;
    }

    // Create the new payload structure with seriesPrograms
    const seriesPayload = {
      name: form.name,
      startDate: form.startDate,
      endDate: form.endDate,
      seriesPrograms: addedPrograms.map(item => ({
        program: { id: item.programId },
        yearOfStudy: { id: item.yearOfStudyId },
        semester: { id: item.semesterId }
      })),
      eimsFetchEnabled: form.eimsFetchEnabled
    };

    try {
      await axios.put(`/api/exam-series/${id}`, seriesPayload);
      setTooltip('Series updated successfully!');
      setTimeout(() => navigate('/admin/exam-series/view'), 2000);
    } catch (err) {
      console.error('Error updating series:', err);
      setTooltip('Error updating series.');
    }
  };

  return (
    <div className="container mt-2">
      <TitleWithBack title="Edit Exam Series" />
      {loading ? <p>Loading...</p> : (
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label htmlFor="seriesName" className="form-label">Series Name</label>
            <input type="text" id="seriesName" name="name" className="form-control" value={form.name} onChange={handleChange} required />
          </div>
          <div className="mb-3">
            <label htmlFor="startDate" className="form-label">Start Date</label>
            <input type="date" id="startDate" name="startDate" className="form-control" value={form.startDate} onChange={handleChange} required />
            {form.startDate && (
            <div className="form-text">Start Date: {formatDate(form.startDate)}</div>
             )}
          </div>
          <div className="mb-3">
            <label htmlFor="endDate" className="form-label">End Date</label>
            <input type="date" id="endDate" name="endDate" className="form-control" value={form.endDate} onChange={handleChange} required />
            {form.endDate && (
            <div className="form-text">End Date: {formatDate(form.endDate)}</div>
             )}
          </div>
          <h5 className="mt-4 mb-3">Programs with Year and Semester</h5>
          <div className="row g-2 mb-3">
            <div className="col-md-4">
              <select
                className="form-select"
                value={selectedProgram}
                onChange={handleProgramChange}
              >
                <option value="">Select Program</option>
                {programs.map(prog => (
                  <option key={prog.id} value={prog.id}>{prog.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={selectedYear}
                onChange={handleYearChange}
              >
                <option value="">Select Year</option>
                {years.map(year => (
                  <option key={year.id} value={year.id}>{year.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={selectedSemester}
                onChange={handleSemesterChange}
                disabled={!selectedProgram || !selectedYear}
              >
                <option value="">Select Semester</option>
                {filteredSemesters.map(sem => (
                  <option key={sem.id} value={sem.id}>{sem.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <button type="button" className="btn btn-primary w-100" onClick={handleAddProgram}>
                Add
              </button>
            </div>
          </div>
          {addedPrograms.length > 0 && (
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>S/N</th>
                    <th>Program</th>
                    <th>Year</th>
                    <th>Semester</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {addedPrograms.map((item, index) => {
                    const prog = programs.find(p => p.id === item.programId);
                    const yearObj = years.find(y => y.id === item.yearOfStudyId);
                    const semObj = semesters.find(s => s.id === item.semesterId);
                    return (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{prog ? prog.name : 'N/A'}</td>
                        <td>{yearObj ? yearObj.name : 'N/A'}</td>
                        <td>{semObj ? semObj.name : 'N/A'}</td>
                        <td>
                          <button type="button" className="btn btn-danger btn-sm" onClick={() => handleRemoveProgram(index)}>
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="form-check mb-3">
            <input
              type="checkbox"
              className="form-check-input"
              id="eimsFetchEnabled"
              checked={form.eimsFetchEnabled}
              onChange={(e) => setForm(prev => ({ ...prev, eimsFetchEnabled: e.target.checked }))}
            />
            <label className="form-check-label" htmlFor="eimsFetchEnabled">
              Enable API Gateway Fetch (allow this series' programs/papers/centres to be pulled automatically)
            </label>
          </div>

          <button type="submit" className="btn btn-primary">Update Series</button>
        </form>
      )}
      {tooltip && (
        <div className="mt-3 alert alert-info">
          {tooltip}
        </div>
      )}
    </div>
  );
}

export default SeriesEdit;
